import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Milestone, User } from "src/models";
import { MilestoneStore, UserStore, AuthStore } from "src/stores";
import { EditData } from ".";
import { CommentView } from "./commentView";
import { FileView } from "./fileView";
import { HistoryView } from "../../../views/log/historyView";
import { RemitView } from "./remitView";
import { DataType, UserRole } from "src/types";
import toast from "react-hot-toast";



export interface IEditMilestoneProps {
  userStore?: UserStore,
  milestoneStore?: MilestoneStore,
  authStore?: AuthStore,
}

export const EditMilestone = inject("userStore", "milestoneStore", "authStore")(observer((props: IEditMilestoneProps): JSX.Element => {
  const { milestoneStore, userStore, authStore } = props;
  const { t, i18n } = useTranslation();
  const ref = React.useRef<{ setActiveView: (view_key: string) => void; }>();
  const m: Milestone = milestoneStore.getActive;
  const projectLeader = [UserRole.Leader, UserRole.Owner].includes(authStore.getCurUser?.getRole(m.room_id));

  if (!m) return null;

  const isCreateByCurrentUser = Number(m.created_by) === authStore.getCurUserID;
  const editableForCurrentUser = m.canEdit && (projectLeader || isCreateByCurrentUser);

  return (
    <EditData
      ref     = {ref}
      data    = {m}
      canEdit  = {editableForCurrentUser}
      onClose   = {async () => {
        const valid = await m.validate();
        if(!valid.length || !editableForCurrentUser) {
          milestoneStore.setActive(null);
        } else {
          toast.error(`${t("failed_to_save_item_missing_properties", {type: t('milestone').toLowerCase()})} ${(valid||[]).map((k:string) => t(k, {ns: 'prop'})).join(', ')} `);
        }
      }}
      body    = {(
        <>
        </>
      )}
      subViews = {[
        {iconId: "chat",    key: "chat",    title: t("comment_plural"),   body: <CommentView />, count: m.total_comments},
        {iconId: "file",    key: "file",    title: t("file_plural"),      body: <FileView    item={m} />,  count: m.total_files},
        {iconId: "history", key: "history", title: t("action_plural"),    body: <HistoryView item={m} />, },
      ]}
      sections={[
        {
          title: t("attribute_plural"),
          entries: [
            {
              type:     "checkbox",
              title:    t("approved"),
              value:    m.approved,
              disabled: !editableForCurrentUser,
              onClick:  async () =>  {
                try {
                  await m.set("approved", !m.approved, true);
                } catch (error) {
                  m.set("approved", false, false);
                  console.log(error)
                  toast.error(t(error), {duration: 10000})
                }
              },
            }, {
              type:     "date",
              title:    t("date"),
              value:    m.date,
              disabled: !editableForCurrentUser,
              onChange: (value) => m.set("date", value, true),
            },
          ]
        }, {
          title: t("connection_plural"),
          entries: [
            {
              type:         "items",
              title:        t("milestone_plural"),
              value:        milestoneStore.links.getArray.filter(mi => m.id != mi.id ),
              itemType:     DataType.Milestone,
              options:      milestoneStore.linkOptions.getArray.map(o => ({ value: o.id, label: o.title })),
              isLoading:    milestoneStore.links.getStatus,
              showLoadMore: milestoneStore.linkOptions.hasMore,
              disabled:     !editableForCurrentUser,
              onAdd:        (link_id: number) => milestoneStore.addItemLink(link_id, DataType.Milestone),
              onRemove:     (link_id: number) => milestoneStore.removeItemLink(link_id, DataType.Milestone),
              onLoad:       () => milestoneStore.linkOptions.load(),
              onOpenSelect: (open: boolean) => open ? milestoneStore.linkOptions.load():                     milestoneStore.linkOptions.clear(),
            }
          ]
        }, {
          title:   t("option_plural"),
          entries: [
            {
              type:     "action",
              title:    t("ics_export"),
              onClick:  m.icsExport,
              iconId:   "calendar",
              disabled: !editableForCurrentUser
            },
            {
              type:     "action",
              title:    t("remove"),
              hidden:   !m.archived,
              iconId:   "delete",
              disabled: !editableForCurrentUser && !m.archived,
              onClick:  async () => {
                try {
                  await m.remove(); milestoneStore.setActive(null);
                  toast.success(t("milestone_removed"));
                } catch (err) {
                  console.error("Failed to remove milestone", err);
                  toast.error(t("failed_remove_milestone"))
                }
              },
            }, {
              type:     "action",
              title:    t("archive"),
              hidden:   m.archived,
              iconId:   "archive",
              disabled: !editableForCurrentUser,
              onClick:  async () => {
                try {
                  await m.archive();
                  toast.success(t("milestone_archived"));
                } catch (err) {
                  console.error("Failed to archive milestone", err);
                  toast.error(t("failed_archive_milestone"))
                }
              },
            }
          ]
        },
      ]}
    />
  )
}));
